import React, { useState } from 'react';

import { Segmented } from 'antd';

import styled from 'styled-components';

import { getMoment, getNow, showDate } from '@libs/NwMoment';

import { getContactName } from '@hooks/API/Contact/UseContactsList';

import BirthdaysDrawer from '@components/Birthdays/BirthdaysDrawer';
import ContactAvatar from '@components/ContactAvatarGroup/ContactAvatar';
import { NwButtonGroup, NwLinkButton } from '@components/Gui/NwButton';
import NwIcon from '@components/Gui/NwIcon';

import { isCurrentPeriod } from '../ScheduleUtils';
import { useAgendaContext } from './AgendaContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FilterTagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.75rem;

    .filter-tag {
        padding: 0.15rem 0.5rem;
        border-radius: 0.75rem;
        font-size: 0.65rem;
        font-weight: 600;
        text-transform: uppercase;
        cursor: pointer;
        transition: all 0.2s ease;
        border: 1px solid ${(props) => props.theme.BorderColor};
        background: white;
        color: ${(props) => props.theme.TextColorMuted};

        &.active {
            background: ${(props) => props.theme.PrimaryColor};
            color: white;
            border-color: ${(props) => props.theme.PrimaryColor};
        }

        &:hover {
            opacity: 0.8;
        }
    }
`;

const BookingAgendaHeaderContainer = styled.div`
    margin: 0.5rem 0;
    padding: 1rem 1rem 0.5rem 1rem;
    background: white;

    width: 100%;

    position: sticky;
    top: 0;
    z-index: 1001;
    box-shadow: 0px 2px 12px -8px rgb(0 0 0 / 40%);

    display: flex;
    flex-direction: column;
    gap: 0;

    .agenda-header-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 2rem;
    }

    div.agenda-header-left {
        padding-left: 1rem;
        flex: 1;
        @media (max-width: 720px) {
            padding-left: 0;
        }

        &.agenda-header-left-model {
            padding-left: 0;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            .agenda-header-left-back {
                font-size: 2rem;
                border-right: ${(props) => props.theme.DefaultBorder};
                padding: 0 1rem;
                margin-right: 1rem;
            }

            .agenda-header-left-avatar {
                padding-right: 1rem;
            }
        }

        h2 {
            font-size: 1rem;
            margin: 0;
            padding: 0;
            font-weight: bold;
        }
        h3 {
            font-size: 0.75rem;
            text-transform: uppercase;
            font-weight: normal;
            margin: 0;
            padding: 0;
            line-height: 1.1em;
        }
        .agenda-header-birthdays {
            cursor: pointer;
            font-size: 0.85rem;
            color: ${(props) => props.theme.PrimaryColor};
            margin: 0.25rem 0;

            &.no-birthdays {
                color: ${(props) => props.theme.TextColorMuted};
                cursor: default;
            }
        }
    }

    div.agenda-header-right {
        width: 8rem;

        div.date-navigator-container {
        }

        div.scroll-top-button-container {
            text-align: right;
            margin-top: 1rem;

            .ant-btn {
                font-size: 0.85rem;
            }
        }
    }
`;

const MobileAgendaHeader = ({ activeFilters = [], onToggleFilter }) => {
    const { bookingSection, contactId, dayBirthDays, onClearContactID, onSelectDate, previewMode, scheduleRange } =
        useAgendaContext();

    const filterOptions = [
        { key: 'job', label: 'Jobs' },
        { key: 'ext', label: 'Ext Jobs' },
        { key: 'casting', label: 'Castings' },
        { key: 'extjob', label: 'Moves' },
        { key: 'activities', label: 'Activities' },
        { key: 'travel', label: 'Travels' },
        { key: 'accommodation', label: 'Accommodations' },
        { key: 'other', label: 'Other' },
    ];

    const dateSelected = scheduleRange ? getMoment(scheduleRange.start) : getNow();

    const selectToday = () => {
        onSelectDate(getNow());
        scrollToTop();
    };

    const selectDayAfter = () => {
        const newdate = dateSelected.clone();
        newdate.add(1, 'days');
        onSelectDate(newdate);
        scrollToTop();
    };

    const selectDayBefore = () => {
        const newdate = dateSelected.clone();
        newdate.subtract(1, 'days');
        onSelectDate(newdate);
        scrollToTop();
    };

    const scrollToTop = () => {
        const drawerBody = document.querySelector('.ant-drawer-body');
        if (drawerBody) {
            const start = drawerBody.scrollTop;
            const duration = 200; // Faster animation (200ms instead of default ~500ms)
            const startTime = performance.now();

            const animateScroll = (currentTime) => {
                const elapsed = currentTime - startTime;
                const progress = Math.min(elapsed / duration, 1);

                // Easing function for smooth deceleration
                const easeOut = 1 - Math.pow(1 - progress, 3);

                drawerBody.scrollTop = start * (1 - easeOut);

                if (progress < 1) {
                    requestAnimationFrame(animateScroll);
                }
            };

            requestAnimationFrame(animateScroll);
        }
    };

    const [showBirthdays, setShowBirthdays] = useState(false);
    const onBirthdayIconClick = () => {
        setShowBirthdays(true);
    };
    const onCloseBirthdayDrawer = () => {
        setShowBirthdays(false);
    };

    return (
        <>
            <BookingAgendaHeaderContainer className="booking-agenda-header-container">
                <div className="agenda-header-top">
                    {contactId && bookingSection ? (
                        <div className="agenda-header-left agenda-header-left-model">
                            <div className="agenda-header-left-back">
                                <NwIcon
                                    pointer
                                    icon={byPrefixAndName.fasl['arrow-left-to-line']}
                                    onClick={onClearContactID}
                                />
                            </div>
                            <div className="agenda-header-left-avatar">
                                <ContactAvatar contactID={contactId} />
                            </div>
                            <div>
                                <h3>{getContactName(contactId)}</h3>
                                <h2>{showDate(dateSelected, 'ext-notime')}</h2>
                            </div>
                        </div>
                    ) : (
                        <div className="agenda-header-left">
                            {!contactId && <h3>agency calendar</h3>}
                            <h2>{showDate(dateSelected, 'ext-notime')}</h2>
                            {dayBirthDays.length > 0 ? (
                                <div className="agenda-header-birthdays" onClick={onBirthdayIconClick}>
                                    <NwIcon icon={byPrefixAndName.fasl['birthday-cake']} />
                                    &nbsp;&nbsp;
                                    <span>
                                        {dayBirthDays.length} Birthday{dayBirthDays.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                            ) : (
                                <div className="agenda-header-birthdays no-birthdays">
                                    <NwIcon icon={byPrefixAndName.fasl['birthday-cake']} />
                                    &nbsp;&nbsp;
                                    <span>No birthdays</span>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="agenda-header-right">
                        <div className="date-navigator-container">
                            <NwButtonGroup
                                capitalize
                                size="small"
                                right
                                buttons={[
                                    { action: selectDayBefore, icon: byPrefixAndName.fasl['chevron-left'] },
                                    {
                                        action: selectToday,
                                        label: 'Today',
                                        disabled: isCurrentPeriod(dateSelected.toDate(), 'agenda'),
                                    },
                                    { action: selectDayAfter, icon: byPrefixAndName.fasl['chevron-right'] },
                                ]}
                            />
                        </div>
                        <div className="scroll-top-button-container">
                            <NwLinkButton
                                label="scroll top"
                                icon={byPrefixAndName.fasl['arrow-up']}
                                onClick={scrollToTop}
                            />
                        </div>
                    </div>
                </div>
                {!contactId && (
                    <FilterTagsContainer>
                        {filterOptions.map((filter) => (
                            <div
                                key={filter.key}
                                className={`filter-tag ${activeFilters.includes(filter.key) ? 'active' : ''}`}
                                onClick={() => onToggleFilter(filter.key)}
                            >
                                {filter.label}
                            </div>
                        ))}
                    </FilterTagsContainer>
                )}
            </BookingAgendaHeaderContainer>
            {showBirthdays && <BirthdaysDrawer birthdays={dayBirthDays} onClose={onCloseBirthdayDrawer} />}
        </>
    );
};

export default MobileAgendaHeader;
